import { Link } from "react-router-dom";
import {
  Loader2, UserCheck, ArrowLeft, ShieldCheck, AlertTriangle,
  ExternalLink, Users,
} from "lucide-react";
import {
  useChiefGetActiveSetQuery,
  useChiefGetExaminersQuery,
  useChiefGetExamCommitteeQuery,
} from "../../api/chiefExaminerApi";

const ROLE_STYLES: Record<string, string> = {
  chairman:  "bg-blue-50 border-blue-100 text-blue-600",
  secretary: "bg-purple-50 border-purple-100 text-purple-600",
  member:    "bg-gray-100 border-gray-200 text-gray-500",
};

export default function ManageExaminersPage() {
  const { data: activeSet, isLoading: loadingSet } = useChiefGetActiveSetQuery();
  const { data: examiners = [], isLoading }        = useChiefGetExaminersQuery();
  const { data: committee = null }                 = useChiefGetExamCommitteeQuery();

  const noActiveSet = !loadingSet && !activeSet;

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4 md:p-6 pb-20">

      {/* ── Stats row ──────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

        {/* Member count */}
        <div className="bg-white border border-gray-100 p-5 rounded-3xl shadow-sm flex items-center gap-4">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center bg-indigo-50 text-indigo-600 shrink-0">
            <UserCheck className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Examiners</div>
            <div className="text-xl font-black text-gray-900">{isLoading ? "…" : examiners.length}</div>
          </div>
        </div>

        {/* Executive set */}
        {!loadingSet && (
          <div className={`border p-5 rounded-3xl shadow-sm flex items-center gap-4 ${
            activeSet ? "bg-emerald-50 border-emerald-100" : "bg-amber-50 border-amber-100"
          }`}>
            <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${
              activeSet ? "bg-emerald-100 text-emerald-600" : "bg-amber-100 text-amber-600"
            }`}>
              {activeSet ? <ShieldCheck className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
            </div>
            <div>
              <div className={`text-[10px] font-black uppercase tracking-widest mb-0.5 ${
                activeSet ? "text-emerald-500" : "text-amber-500"
              }`}>Executive Set</div>
              <div className={`text-sm font-black leading-tight ${
                activeSet ? "text-emerald-800" : "text-amber-700"
              }`}>
                {activeSet ? activeSet.name : "None active"}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* No active set warning */}
      {noActiveSet && (
        <div className="flex items-start gap-4 bg-amber-50 border border-amber-200 rounded-3xl px-6 py-5">
          <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
          <p className="text-amber-700 text-xs font-medium">
            No active executive set. Ask an administrator to activate the current executive set.
          </p>
        </div>
      )}

      {/* ── Page header ────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <Link to="/exams/chief"
              className="p-3 rounded-xl border bg-white hover:bg-gray-50 transition-all shadow-sm">
              <ArrowLeft className="w-4 h-4 text-gray-500" />
            </Link>
            <div className="w-14 h-14 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl shadow-gray-200">
              <Users className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-gray-900 tracking-tight leading-none uppercase">
                {committee?.name ?? "Examination Board"}
              </h1>
              <p className="text-gray-400 text-sm mt-1.5 font-medium">
                {activeSet ? `Members · ${activeSet.name}` : "Active executive set members"}
              </p>
            </div>
          </div>

          {committee && (
            <Link
              to={`/committees/${committee.id}`}
              className="inline-flex items-center gap-2 border border-gray-200 bg-white text-gray-700 rounded-2xl px-5 py-2.5 text-[10px] font-black uppercase tracking-[0.15em] hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Manage Committee
            </Link>
          )}
        </div>
      </div>

      {/* ── Examiners list ─────────────────────────────────────────── */}
      <div className="bg-white border border-gray-100 rounded-[2.5rem] shadow-sm overflow-hidden">
        <div className="px-8 py-5 border-b border-gray-50">
          <div className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
            Committee Roster
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-gray-200" />
          </div>
        ) : !committee && !isLoading ? (
          <div className="py-24 text-center space-y-3">
            <AlertTriangle className="w-10 h-10 mx-auto text-amber-200" />
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">
              Examination Board committee not found
            </p>
            <p className="text-xs text-gray-300 font-medium">
              Create an "Examination Board" committee for the active executive set.
            </p>
          </div>
        ) : examiners.length === 0 ? (
          <div className="py-24 text-center">
            <UserCheck className="w-10 h-10 mx-auto text-gray-100 mb-3" />
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">
              No members in this committee
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {examiners.map((ex: any) => (
              <div key={ex.id}
                className="flex items-start gap-4 px-8 py-5 hover:bg-gray-50/80 transition-all">

                {/* Avatar */}
                <div className="w-10 h-10 bg-gray-900 rounded-2xl flex items-center justify-center text-white font-black text-sm shrink-0 mt-0.5">
                  {ex.name?.[0] ?? "?"}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-0.5">
                    <p className="font-black text-gray-900 uppercase tracking-tight">
                      {ex.name}
                    </p>
                    <span className={`inline-block px-2 py-0.5 rounded-md text-[9px] font-black uppercase border ${
                      ROLE_STYLES[ex.committee_role] ?? ROLE_STYLES.member
                    }`}>
                      {ex.committee_role}
                    </span>
                  </div>

                  {ex.position && (
                    <p className="text-[10px] font-bold text-indigo-500 uppercase tracking-widest mb-1">
                      {ex.position}
                    </p>
                  )}

                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                    {ex.email}
                    {ex.phone ? ` · ${ex.phone}` : ""}
                    {ex.membership_no ? ` · ${ex.membership_no}` : ""}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer note */}
      <div className="bg-gray-900 text-white rounded-[2.5rem] p-10 flex items-center gap-8 shadow-2xl shadow-gray-200">
        <div className="w-16 h-16 bg-indigo-500/10 rounded-3xl flex items-center justify-center shrink-0 border border-indigo-500/20">
          <ShieldCheck className="w-8 h-8 text-indigo-400" />
        </div>
        <div>
          <h4 className="text-[11px] font-black uppercase tracking-[0.4em] text-indigo-400 mb-2">
            Registry Policy
          </h4>
          <p className="text-sm text-gray-400 leading-relaxed font-medium max-w-3xl">
            Examiners listed here are members of the Examination Board committee for the active executive set.
            To add or remove examiners, use the{" "}
            <strong className="text-white">Manage Committee</strong> button above.
          </p>
        </div>
      </div>

    </div>
  );
}
